import { FC } from 'react'

import styled from 'styled-components'

import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'
import {
  useCampaignVideo,
  useSetCampaignVideoRateMutation,
} from 'src/Redux/campaignVideoApi'
import dayjs from 'src/Helpers/dayjs'
import { useReels } from 'src/Hooks/useReels'

import { RateFormData } from './Components/RateBlock'
import { VideoDetails } from './Components/VideoDetails'
import { TikTokPlayer } from './Components/TiktokPlayer'
import { TikTokVideoDetailsBreadcrumbs } from './Components/TikTokVideoDetailsBreadcrumbs'
import { useHandleRateEvents } from './useHandleRateEvents'

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    gap: 48px;
    padding-bottom: 80px;
  }
`

const ContentContainer = styled.div`
  display: flex;
  max-width: 1120px;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    gap: 48px;
    flex-direction: column;
    align-items: center;
  }
`

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  flex: 1 0 0;

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    gap: 48px;
  }
`

const DAYS_BETWEEN_MIN_VIEWS_DEADLINE_AND_PUBLISH_DATE = 5
const HOURS_BETWEEN_REVIEWS_DEADLINE_AND_PUBLISH_DATE = 72

export const TikTokVideoDetailsPage: FC = () => {
  const { video, isLoading } = useCampaignVideo()
  const { isDetailsForReelsCampaign } = useReels()
  const [onRateEvents] = useHandleRateEvents()
  const [setCampaignVideoRate, { isLoading: isRateLoading }] =
    useSetCampaignVideoRateMutation({
      fixedCacheKey: 'setCampaignVideoRate',
    })

  const onRateSubmit = (value: RateFormData): void => {
    if (video?.id) {
      setCampaignVideoRate({
        id: video?.id,
        feedback: value.comment,
        stars: value.rate,
      })
        .unwrap()
        .then((res) => {
          onRateEvents({
            data: res,
            videoID: video.id,
            feedback: value.comment,
            videoLink: video.videoLink,
          })
        })
    }
  }

  const videoPublishedDate = dayjs(video?.minViewsDeadline)
    .subtract(DAYS_BETWEEN_MIN_VIEWS_DEADLINE_AND_PUBLISH_DATE, 'day')
    .toString()

  const approvedAtDate = dayjs(video?.videoUploadDate || '')
    .add(HOURS_BETWEEN_REVIEWS_DEADLINE_AND_PUBLISH_DATE, 'hour')
    .toString()

  const isAvailabilityCaseOpenAt = Boolean(video?.availabilityCaseOpenAt)
  const isLoweredViewsCaseOpenAt = Boolean(video?.loweredViewsCaseOpenAt)

  return (
    <PageContainer>
      <TikTokVideoDetailsBreadcrumbs />
      <ContentContainer>
        <TikTokPlayer url={video?.videoLink} />
        <InfoContainer>
          <VideoDetails
            userPhotoUrl={video?.userPhotoUrl}
            firstName={video?.nickname}
            followers={video?.followerCount}
            currentViews={video?.viewsCount}
            minViews={video?.minViews}
            videoStatus={video?.status}
            videoPublishedDate={videoPublishedDate}
            milestoneDate={video?.minViewsDeadline}
            dismissReason={video?.dismissReason}
            loading={isLoading}
            tiktokProfileUrl={video?.tiktokProfileUrl}
            videoPreModerationStatus={video?.preModerationStatus}
            isAvailabilityCaseOpenAt={isAvailabilityCaseOpenAt}
            isLoweredViewsCaseOpenAt={isLoweredViewsCaseOpenAt}
            commentType={!isDetailsForReelsCampaign ? video?.commentType : null}
            rateDeadline={approvedAtDate}
            onRateSubmit={onRateSubmit}
            rate={video?.stars}
            isRateLoading={isRateLoading}
            feedback={video?.feedback}
          />
        </InfoContainer>
      </ContentContainer>
    </PageContainer>
  )
}
